// Lib
import { useEffect, MutableRefObject } from 'react';
import { sum } from 'lodash';
import Handsontable from 'handsontable/base';

// Utils
import { RectSize } from '../../../../common/maths/geometry/rect/rectTypes';
import useThrottledCallback from '../../../utils/react/useThrottledCallback';
import { dangerouslySetTableElementWidthPx } from '../utils/domUtils/hotTableDomMutationUtils';
import { getHotColWidthsGu } from '../utils/hotInstanceUtils/columnSize/hotTableColWidthGetterUtils';
import { resizeTable } from '../utils/hotInstanceUtils/grid/hotTableGridResizeUtils';
import { TableChangeSource } from '../../../../common/table/TableTypes';

interface UseTableResizeHandlersProps {
    gridSize: number;
    isResizing: boolean;
    tempSize: RectSize | null;
    hotTableInstanceRef: MutableRefObject<Handsontable.Core | null>;
    getContextZoomScale: () => number;
}

/**
 * Custom hook for handling table resize operations with drag handle
 *
 * @param props - Hook configuration
 * @returns Handler functions for table resizing
 */
export const useTableResizeEffects = (props: UseTableResizeHandlersProps) => {
    const { gridSize, isResizing, tempSize, hotTableInstanceRef, getContextZoomScale } = props;

    // Adjust columns and rows with drag handle
    const throttledAdjustTableSize = useThrottledCallback(
        (): void => {
            if (!isResizing || !tempSize) return;

            const hot = hotTableInstanceRef.current;
            if (!hot || hot.isDestroyed) return;

            const currentColWidths = getHotColWidthsGu(hot, gridSize);
            const { rowDif, columnDif } =
                resizeTable(hot, currentColWidths, tempSize, gridSize, getContextZoomScale) ?? {};

            const source = TableChangeSource.RESIZE_HANDLE;
            const nRows = hot.countRows();
            const nCols = hot.countCols();

            if (!rowDif && !columnDif) return;

            // When adjusting table size, alter both rows and columns in 1 batched render
            hot.batch(() => {
                // Adjust rows
                if (rowDif > 0) {
                    hot.alter('insert_row_below', nRows, rowDif, source);
                } else if (rowDif < 0) {
                    const numberToRemove = -rowDif;
                    hot.alter('remove_row', nRows - numberToRemove, numberToRemove, source);
                }

                // For column changes we also need to update column and table widths
                if (!columnDif) return;

                // Adjust columns
                if (columnDif > 0) {
                    hot.alter('insert_col_end', nCols, columnDif, source);
                } else if (columnDif < 0) {
                    const numberToRemove = -columnDif;
                    hot.alter('remove_col', nCols - numberToRemove, numberToRemove, source);
                }

                // Adjust table width
                const newColWidthsGu = getHotColWidthsGu(hot, gridSize);
                const newTableWidthGu = sum(newColWidthsGu);
                const newTableWidthPx = newTableWidthGu * gridSize - 2;

                dangerouslySetTableElementWidthPx(hot, newTableWidthPx);
            });
        },
        20,
        [isResizing, tempSize, hotTableInstanceRef, gridSize, getContextZoomScale],
    );

    // When tempSize changes, throttle adjust table size
    useEffect(() => {
        throttledAdjustTableSize();
    }, [tempSize]);
};

export default useTableResizeEffects;
